import { useTranslation } from 'react-i18next';

import { MetricSummaryRow } from '@/components/molecules/MetricSummaryRow';
import type { DistributionStats } from '@/domain/cages';
import { localizedDateFormat } from '@/utils/date';
import { formatNumberByLocale, parseLocale } from '@/utils/localeFormatters';

import { formatCount } from '../weightDistributionChartSpec';

import styles from './WeightDistributionSummary.module.scss';

const WEIGHT_DECIMALS = 0;
const CV_DECIMALS = 1;
const SHAPE_DECIMALS = 3;
const NO_VALUE = '-';

export interface WeightDistributionSummaryProps {
    stats: DistributionStats;
    /** Day the stats belong to, `YYYY-MM-DD`. */
    day: string;
    /**
     * Measurements behind the histogram on screen, or `null` when there is none.
     *
     * Not part of `stats`, which mirrors the API's per-type datum one to one.
     * How many days this covers depends on the Aggregated toggle, so the row is
     * labelled without a period.
     */
    measurementCount: number | null;
    weightUnitTag: string;
    /** Smolt cages get the largest and smallest fish estimates. */
    isSmolt: boolean;
    /**
     * The named-fish easter egg only shows on the most recent day, so it does not
     * follow the user around while they scrub back through the series.
     */
    showNamedFish: boolean;
    /** Stable for the lifetime of the page, so the fish keeps its name. */
    fishName: string;
    isLoading: boolean;
}

/**
 * Distribution statistics for the day currently on the chart.
 *
 * Plain rows, with a skeleton per row while the day loads.
 */
export default function WeightDistributionSummary(props: WeightDistributionSummaryProps) {
    const {
        stats,
        day,
        measurementCount,
        weightUnitTag,
        isSmolt,
        showNamedFish,
        fishName,
        isLoading,
    } = props;
    const { t, i18n } = useTranslation();
    const locale = parseLocale(i18n.language);

    function formatWeight(value: number | null | undefined): string {
        return value == null
            ? NO_VALUE
            : `${formatNumberByLocale(value, locale, WEIGHT_DECIMALS)} ${weightUnitTag}`;
    }

    const dayLabel = day
        ? localizedDateFormat({ dateString: day, onlydate: true, locale: i18n.language })
        : NO_VALUE;

    return (
        <>
            <MetricSummaryRow
                label={t('common.labels.date')}
                value={dayLabel}
                isLoading={isLoading}
            />
            <MetricSummaryRow
                label={t('common.labels.livingWeight')}
                value={formatWeight(stats.weight)}
                isLoading={isLoading}
            />
            <MetricSummaryRow
                label={t('cageWeight.weightDistribution.measurements')}
                value={measurementCount == null ? NO_VALUE : formatCount(measurementCount, locale)}
                isLoading={isLoading}
            />
            <MetricSummaryRow
                label={t('cageWeight.distribution.cv')}
                value={
                    stats.cv == null
                        ? NO_VALUE
                        : `${formatNumberByLocale(stats.cv, locale, CV_DECIMALS)} %`
                }
                isLoading={isLoading}
            />
            <MetricSummaryRow
                label={t('cageWeight.distribution.skewness')}
                value={
                    stats.skewness == null
                        ? NO_VALUE
                        : formatNumberByLocale(stats.skewness, locale, SHAPE_DECIMALS)
                }
                isLoading={isLoading}
                tooltip={t('descriptions.skewness')}
                tooltipTargetClassName={styles.tooltipContainer}
            />
            <MetricSummaryRow
                label={t('cageWeight.distribution.kurtosis')}
                value={
                    stats.kurtosis == null
                        ? NO_VALUE
                        : formatNumberByLocale(stats.kurtosis, locale, SHAPE_DECIMALS)
                }
                isLoading={isLoading}
                tooltip={t('descriptions.kurtosis')}
                tooltipTargetClassName={styles.tooltipContainer}
            />
            {isSmolt && (
                <>
                    <MetricSummaryRow
                        label={t('cageWeight.distribution.largestFish')}
                        value={`${t('cageWeight.distribution.approximately')} ${formatWeight(stats.largestFish ?? 0)}`}
                        isLoading={isLoading}
                    />
                    <MetricSummaryRow
                        label={t('cageWeight.distribution.smallestFish')}
                        value={`${t('cageWeight.distribution.approximately')} ${formatWeight(stats.smallestFish ?? 0)}`}
                        isLoading={isLoading}
                    />
                    {!isLoading && showNamedFish && (
                        <div className={styles.footnote}>
                            {`(${t('cageWeight.distribution.weNamedThisTinySwimmer')} ${fishName} 🐟)`}
                        </div>
                    )}
                </>
            )}
        </>
    );
}
